import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  inject,
  DestroyRef,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';

import { UtilizadorService } from '@core/services/domain-services';
import { NifService } from '@core/services/nif.service';
import { FeedbackService } from '@core/services/feedback.service';
import { extractErrorMessage } from '@core/utils/error-parser.util';
import { compressImageIfNeeded } from '@shared/utils/compress-image.util';

const MAX_FOTO_SIZE = 5 * 1024 * 1024;
const ALLOWED_FOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

@Component({
  selector: 'app-criar-utilizador',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DrawerModule,
    ButtonModule,
    InputTextModule,
    Select,
  ],
  templateUrl: './criar-utilizador.component.html',
  styleUrl: './criar-utilizador.component.scss',
})
export class CriarUtilizadorComponent implements OnChanges {
  @Input() visible = false;
  @Input() defaultPerfil: string = 'EFETIVO';
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<void>();

  userForm!: FormGroup;
  loading = false;
  submitted = false;

  perfilOptions = [
    { label: 'Efetivo', value: 'EFETIVO' },
    { label: 'Cliente', value: 'CLIENTE' },
    { label: 'Admin', value: 'ADMIN' },
  ];

  selectedFoto: File | null = null;
  fotoPreviewUrl: string | null = null;

  private readonly fb = inject(FormBuilder);
  private readonly feedback = inject(FeedbackService);
  private readonly userSvc = inject(UtilizadorService);
  private readonly nifSvc = inject(NifService);
  private readonly destroyRef = inject(DestroyRef);

  biSearching = false;
  private nifAutoFilled = false;

  constructor() {
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      const perfilInicial = (this.defaultPerfil || 'EFETIVO').toUpperCase();
      this.userForm.patchValue({
        tipoPerfil: ['EFETIVO', 'CLIENTE', 'ADMIN'].includes(perfilInicial)
          ? perfilInicial
          : 'EFETIVO',
      });
    }
  }

  private initForm(): void {
    this.userForm = this.fb.group({
      userName: ['', [Validators.required, Validators.minLength(3)]],
      nome: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      tipoPerfil: ['EFETIVO', Validators.required],
    });

    this.userForm
      .get('userName')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.nifAutoFilled) {
          this.nifAutoFilled = false;
          this.userForm.patchValue({ nome: '' });
        }
      });
  }

  searchNIF(): void {
    const nif = this.userForm.get('userName')?.value?.trim();
    if (!nif) {
      this.feedback.showWarning('Introduza o NIF antes de pesquisar.', {
        summary: 'NIF em Falta',
        life: 3000,
      });
      return;
    }
    this.biSearching = true;
    this.nifSvc
      .lookup(nif)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (c) => {
          this.userForm.patchValue({ nome: c.nome });
          this.nifAutoFilled = true;
          this.feedback.showSuccess(`Nome preenchido: ${c.nome}`, {
            summary: 'NIF Encontrado',
            life: 3000,
          });
          this.biSearching = false;
        },
        error: (error) => {
          this.feedback.showInfo(
            error?.retorno?.mensagem ||
              'Nenhum registo encontrado para este NIF. Pode preencher os dados manualmente.',
            { summary: 'NIF Não Encontrado', life: 4000 },
          );
          this.biSearching = false;
        },
      });
  }

  get f() {
    return this.userForm.controls;
  }

  async onFotoSelect(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];

    if (file.size > MAX_FOTO_SIZE) {
      this.feedback.showError('Fotografia muito grande. Tamanho máximo: 5MB.', {
        summary: 'Ficheiro Inválido',
        life: 4000,
      });
      input.value = '';
      return;
    }
    if (!ALLOWED_FOTO_TYPES.includes(file.type)) {
      this.feedback.showError('Formato inválido. Use JPG, PNG ou WEBP.', {
        summary: 'Formato Não Suportado',
        life: 4000,
      });
      input.value = '';
      return;
    }

    const compressedFile = await compressImageIfNeeded(file);
    this.selectedFoto = compressedFile;
    if (this.fotoPreviewUrl) {
      URL.revokeObjectURL(this.fotoPreviewUrl);
    }
    this.fotoPreviewUrl = URL.createObjectURL(compressedFile);
  }

  removeFoto(): void {
    if (this.fotoPreviewUrl) {
      URL.revokeObjectURL(this.fotoPreviewUrl);
    }
    this.selectedFoto = null;
    this.fotoPreviewUrl = null;
  }

  closeDrawer(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.userForm.reset({ tipoPerfil: 'EFETIVO' });
    this.nifAutoFilled = false;
    this.removeFoto();
    this.submitted = false;
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.userForm.invalid) {
      this.feedback.showWarning('Por favor, preencha todos os campos obrigatórios.', {
        summary: 'Formulário Incompleto',
        life: 4000,
      });
      return;
    }

    this.loading = true;
    const formValues = this.userForm.getRawValue();

    const formData = new FormData();
    formData.append('Nome', formValues.nome?.trim() ?? '');
    formData.append('Email', formValues.email?.trim() ?? '');
    formData.append('UserName', formValues.userName?.trim() ?? '');
    if (formValues.tipoPerfil) {
      formData.append('TipoPerfilEnum', formValues.tipoPerfil);
    }
    if (this.selectedFoto) {
      formData.append('Fotografia', this.selectedFoto, this.selectedFoto.name);
    }

    this.userSvc
      .criarConta(formData)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (res) => {
          const detail = this.feedback.extractSuccessMessage(
            res,
            'Conta criada com sucesso. O utilizador irá receber as credenciais e deverá definir a palavra-passe no primeiro acesso.',
          );
          this.feedback.showSuccess(detail, {
            summary: 'Utilizador Criado',
            life: 6000,
          });
          this.saved.emit();
          setTimeout(() => this.closeDrawer(), 1000);
        },
        error: (error: unknown) => {
          const mensagemBackend = extractErrorMessage(error, 'Não foi possível criar o utilizador.');
          const mensagem = /perfil\s+j[aá]\s+existe|e-?mail.*(existe|utilizado)|email.*(existe|utilizado)/i.test(mensagemBackend)
            ? 'E-mail já existente no sistema'
            : mensagemBackend;
          this.feedback.showError(mensagem, {
            summary: mensagem === 'E-mail já existente no sistema' ? 'E-mail já existente' : 'Erro ao criar utilizador',
            life: 5000,
          });
        },
      });
  }
}
